import {ChevronDown} from 'lucide-react'
import {Tooltip} from '~/src/design-system/BaseComponents'
import {Avatar, Button} from '~/src/design-system/Primitives'
import {useNavigate} from 'react-router-dom'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import type {IUserBase} from '~/src/models'
import {useLazyGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {useSignoutMutation} from '~/src/store/apis/auth-api'
import {notificationsApi} from '~/src/store/apis/notifications-api'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import ChangePasswordDialog from '../dialog/ChangePasswordDialog'
import EulaDialog from './EulaDialog'
import {roleApi} from '~/src/services/roleApi'
import {useVisibleSettingsPages} from '~/src/components/platform/settings/settingsPages'

interface UserMenuProps {
  currentUser: IUserBase
}

const MenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`

const DropdownMenu = styled.div<{$open: boolean}>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  z-index: 1000;
  min-width: 220px;
  background: var(--white);
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-sm);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  padding: var(--space-1) 0;
  display: ${({$open}) => ($open ? 'block' : 'none')};
`

const DropdownItem = styled.button<{$nonInteractive?: boolean}>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-4);
  border: none;
  background: none;
  font-size: 14px;
  color: var(--neutral-800);
  cursor: ${({$nonInteractive}) => ($nonInteractive ? 'default' : 'pointer')};
  text-align: left;

  &:hover {
    background: ${({$nonInteractive}) => ($nonInteractive ? 'none' : 'var(--neutral-100)')};
  }

  &:hover .itemText {
    color: ${({$nonInteractive}) => ($nonInteractive ? 'inherit' : 'var(--brand-cta)')};
  }
`

const StyledText = styled.span`
  font-style: normal;
  font-weight: var(--fw-regular);
  font-size: var(--fs-caption);
  line-height: 19px;
  color: #383838;
`

const StyledLightText = styled.span`
  font-style: normal;
  font-weight: var(--fw-regular);
  font-size: 11px;
  line-height: 13px;
  color: var(--neutral-600);
`

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  padding: var(--space-2) var(--space-4);
  margin-top: 37px;
`

const UserMenu = ({currentUser}: UserMenuProps) => {
  const [open, setOpen] = useState(false)
  const [isEulaOpen, setIsEulaOpen] = useState(false)
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [signout] = useSignoutMutation()
  const [getAgency, {error: agencyError}] = useLazyGetOneAgencyQuery()
  const [stateRole, newStateRole] = useState('')
  const {loggedInAgencyId} = useAppSelector((state) => state.root)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleToggleMenu = () => {
    setOpen((prev) => !prev)
  }

  const handleCloseUserMenu = useCallback(() => {
    setOpen(false)
  }, [])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const handleSignOut = async () => {
    dispatch(notificationsApi.util.resetApiState())
    await signout({email: currentUser.emailAddress}).unwrap()
    navigate('/signin')
  }

  const handleCloseEula = useCallback(() => {
    setIsEulaOpen(false)
  }, [setIsEulaOpen])

  const handleOpenEula = useCallback(() => {
    handleCloseUserMenu()
    setIsEulaOpen(true)
  }, [handleCloseUserMenu])

  const handleCloseChangePassword = useCallback(() => {
    setIsChangePasswordOpen(false)
  }, [])

  const handleOpenChangePassword = useCallback(() => {
    handleCloseUserMenu()
    setIsChangePasswordOpen(true)
  }, [handleCloseUserMenu])

  const {emailAddress, firstName, lastName, roles} = currentUser

  // STFFLG-4185: offered iff the role holds at least one Settings page, the same list the Settings
  // nav shows (it was a role-NAME check that hid it from candidates only). Candidates hold none.
  const {pages: settingsPages} = useVisibleSettingsPages()
  const hasSettings = settingsPages.length > 0

  useEffect(() => {
    async function fetchData() {
      const roleName = roles[0]

      // Handle agency admin with MSP check
      if (roleName === 'agencyadmin') {
        const agency = await getAgency(loggedInAgencyId).unwrap()
        return agency.isMsp ? newStateRole('MSP User (me)') : newStateRole('Agency User (me)')
      }

      // Handle known system roles
      const knownRoles: Record<string, string> = {
        general_admin: 'General Admin (me)',
        companyadmin: 'Company User (me)',
        platformadmin: 'Platform User (me)',
        mainagencyadmin: 'Main Agency Admin (me)',
      }

      if (knownRoles[roleName]) {
        return newStateRole(knownRoles[roleName])
      }

      // For custom roles, fetch role details to get category
      try {
        const role = await roleApi.getRoleByName(roleName)

        // Map category to user-friendly label
        const categoryLabels: Record<string, string> = {
          Platform: 'Platform User',
          MSP: 'MSP User',
          Agency: 'Agency User',
          Company: 'Company User',
        }

        const categoryLabel = categoryLabels[role.category] || 'User'
        return newStateRole(`${categoryLabel} (me)`)
      } catch {
        // Fallback if role fetch fails
        return newStateRole('User (me)')
      }
    }
    void fetchData()
  }, [getAgency, loggedInAgencyId, roles])

  return (
    <>
      <MenuWrapper ref={menuRef}>
        <Tooltip content="Open settings" side="bottom">
          <Button
            variant="ghost"
            onClick={handleToggleMenu}
            style={{padding: 0}}
            data-testid="user-menu-button">
            <Avatar
              initials={`${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()}
              isUser
            />
            <ChevronDown size={16} style={{color: 'var(--neutral-600)'}} />
          </Button>
        </Tooltip>
        <DropdownMenu $open={open} data-testid="user-menu-dropdown">
          <DropdownItem $nonInteractive data-testid="user-menu-role-item">
            <StyledLightText>{stateRole}</StyledLightText>
          </DropdownItem>
          <DropdownItem onClick={handleOpenChangePassword} data-testid="user-menu-change-password">
            <StyledText className="itemText">Change Password</StyledText>
          </DropdownItem>
          <DropdownItem onClick={handleOpenEula} data-testid="user-menu-eula">
            <StyledText className="itemText">EULA</StyledText>
          </DropdownItem>
          {hasSettings && (
            <DropdownItem
              onClick={() => {
                handleCloseUserMenu()
                navigate('settings')
              }}
              data-testid="user-menu-settings">
              <StyledText className="itemText">Settings</StyledText>
            </DropdownItem>
          )}
          <DropdownItem onClick={handleSignOut} data-testid="user-menu-logout">
            <StyledText className="itemText">Log Out</StyledText>
          </DropdownItem>
          <ProfileSection data-testid="user-menu-profile-info">
            <Avatar
              alt={`${firstName} ${lastName}`}
              initials={`${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()}
              isUser
              style={{marginRight: '13px'}}
            />
            <StyledLightText>
              {emailAddress}
              <br />
              {firstName} {lastName}
            </StyledLightText>
          </ProfileSection>
        </DropdownMenu>
      </MenuWrapper>
      <EulaDialog isModalOpen={isEulaOpen} onModalClose={handleCloseEula} />
      <ChangePasswordDialog
        isOpen={isChangePasswordOpen}
        handleCancel={handleCloseChangePassword}
      />
    </>
  )
}

export default UserMenu
